import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faCompressAlt, faObjectGroup, faPalette } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useFocusWithin } from "@vueuse/core";
import { BButton, BButtonGroup } from "bootstrap-vue";
import { sanitize } from "dompurify";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { AxisAlignedBoundingBox } from "@/components/Workflow/Editor/modules/geometry";
import { useWorkflowStores } from "@/composables/workflowStores";
import { LazyMoveMultipleAction } from "../Actions/workflowActions";
import { brighterColors, darkenedColors } from "./colors";
import { useResizable } from "./useResizable";
import { selectAllText } from "./utilities";
import ColorSelector from "./ColorSelector.vue";
import DraggablePan from "@/components/Workflow/Editor/DraggablePan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FrameComment',
    props: {
        comment: null,
        rootOffset: null,
        scale: null,
        readonly: { type: Boolean }
    },
    emits: ["change", "resize", "move", "pan-by", "remove", "set-color"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faObjectGroup, faTrashAlt, faPalette, faCompressAlt);
        const resizeContainer = ref();
        useResizable(resizeContainer, computed(() => props.comment.size), ([width, height]) => {
            emit("resize", [width, height]);
        });
        function escapeAndSanitize(text) {
            return sanitize(text, { ALLOWED_TAGS: [] }).replace(/(?:^(\s|&nbsp;)+)|(?:(\s|&nbsp;)+$)/g, "");
        }
        const editableElement = ref();
        function getInnerText() {
            const element = editableElement.value;
            if (element) {
                const value = element.innerHTML ?? "";
                return escapeAndSanitize(value);
            }
            else {
                return "";
            }
        }
        function saveText() {
            const text = getInnerText();
            if (text !== props.comment.data.title) {
                emit("change", { ...props.comment.data, title: text });
            }
        }
        const showColorSelector = ref(false);
        const rootElement = ref();
        const { focused } = useFocusWithin(rootElement);
        watch(() => focused.value, () => {
            if (!focused.value) {
                showColorSelector.value = false;
            }
        });
        function onClick() {
            editableElement.value?.focus();
        }
        function onSetColor(color) {
            emit("set-color", color);
        }
        const { stateStore, stepStore, commentStore, undoRedoStore } = useWorkflowStores();
        function getStepsInBounds(bounds) {
            const steps = [];
            Object.values(stepStore.steps).forEach((step) => {
                const rect = stateStore.stepPosition[step.id];
                if (rect && step.position) {
                    const stepRect = {
                        x: step.position.left,
                        y: step.position.top,
                        width: rect.width,
                        height: rect.height,
                    };
                    if (bounds.contains(stepRect)) {
                        steps.push(step);
                    }
                }
            });
            return steps;
        }
        function getCommentsInBounds(bounds) {
            const comments = [];
            commentStore.comments.forEach((comment) => {
                const commentRect = {
                    x: comment.position[0],
                    y: comment.position[1],
                    width: comment.size[0],
                    height: comment.size[1],
                };
                if (comment !== props.comment && bounds.contains(commentRect)) {
                    comments.push(comment);
                }
            });
            return comments;
        }
        let lazyAction = null;
        function getAABB() {
            const aabb = new AxisAlignedBoundingBox();
            aabb.x = props.comment.position[0];
            aabb.y = props.comment.position[1];
            aabb.width = props.comment.size[0];
            aabb.height = props.comment.size[1];
            return aabb;
        }
        let resampleNodes = true;
        let stepsInBounds = [];
        let commentsInBounds = [];
        function onDrag() {
            const aabb = getAABB();
            if (resampleNodes) {
                stepsInBounds = getStepsInBounds(aabb);
                commentsInBounds = getCommentsInBounds(aabb);
                commentsInBounds.push(props.comment);
                resampleNodes = false;
            }
            lazyAction = new LazyMoveMultipleAction(commentStore, stepStore, commentsInBounds, stepsInBounds, aabb);
            undoRedoStore.applyLazyAction(lazyAction);
        }
        function onDragEnd() {
            resampleNodes = true;
            saveText();
            undoRedoStore.flushLazyAction();
        }
        function onMove(position) {
            if (lazyAction && undoRedoStore.isQueued(lazyAction)) {
                lazyAction.changePosition(position);
            }
            else {
                onDrag();
            }
        }
        function onDoubleClick() {
            if (editableElement.value) {
                selectAllText(editableElement.value);
            }
        }
        function onFitToContent() {
            const aabb = getAABB();
            const stepsInBounds = getStepsInBounds(aabb);
            const commentsInBounds = getCommentsInBounds(aabb);
            const targetAABB = new AxisAlignedBoundingBox();
            stepsInBounds.forEach((step) => {
                const rect = stateStore.stepPosition[step.id];
                if (rect && step.position) {
                    const stepRect = {
                        x: step.position.left,
                        y: step.position.top,
                        width: rect.width,
                        height: rect.height,
                    };
                    targetAABB.fitRectangle(stepRect);
                }
            });
            commentsInBounds.forEach((comment) => {
                const commentRect = {
                    x: comment.position[0],
                    y: comment.position[1],
                    width: comment.size[0],
                    height: comment.size[1],
                };
                targetAABB.fitRectangle(commentRect);
            });
            targetAABB.expand(20);
            targetAABB.y -= 20;
            emit("move", [targetAABB.x, targetAABB.y]);
            emit("resize", [targetAABB.width, targetAABB.height]);
        }
        const cssVariables = computed(() => {
            const vars = {};
            if (props.comment.color !== "none") {
                vars["--primary-color"] = darkenedColors[props.comment.color];
                vars["--secondary-color"] = brighterColors[props.comment.color];
            }
            return vars;
        });
        onMounted(() => {
            if (commentStore.isJustCreated(props.comment.id) && editableElement.value) {
                selectAllText(editableElement.value);
            }
        });
        const position = computed(() => ({ x: props.comment.position[0], y: props.comment.position[1] }));
        return { __sfc: true, props, emit, resizeContainer, escapeAndSanitize, editableElement, getInnerText, saveText, showColorSelector, rootElement, focused, onClick, onSetColor, stateStore, stepStore, commentStore, undoRedoStore, getStepsInBounds, getCommentsInBounds, lazyAction, getAABB, resampleNodes, stepsInBounds, commentsInBounds, onDrag, onDragEnd, onMove, onDoubleClick, onFitToContent, cssVariables, position, FontAwesomeIcon, BButton, BButtonGroup, reactive, ColorSelector, DraggablePan };
    }
});
